export const METRICS = {
    energy: (f) => f.energy ?? 0,
    centroid: (f) => f.centroid ?? 0,
    voicing: (f) => f.voicing ?? 0,
    flatness: (f) => f.flatness ?? 0,
};
export class SideStats {
    constructor() {
        Object.defineProperty(this, "n", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "peak", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "mean", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "m2", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
    }
    get mu() {
        return this.n > 0 ? this.mean : 0;
    }
    get sigma() {
        return this.n > 1 ? Math.sqrt(Math.max(0, this.m2 / this.n)) : 0;
    }
    push(x) {
        this.n += 1;
        const d = x - this.mean;
        this.mean += d / this.n;
        this.m2 += d * (x - this.mean);
        if (x > this.peak)
            this.peak = x;
    }
    reset() {
        this.n = 0;
        this.mean = 0;
        this.m2 = 0;
        this.peak = 0;
    }
}
export function separability(inS, outS) {
    if (inS.n < 2 || outS.n < 2)
        return 0;
    const denom = inS.sigma + outS.sigma + 1e-4;
    return (inS.mu - outS.mu) / denom;
}
export function saturationPenalty(inS) {
    const over = Math.max(0, inS.mu - 0.9) / 0.1;
    const pinned = inS.sigma < 0.03 ? 1 : 0;
    return over * (0.5 + 0.5 * pinned) * 3;
}
export const DEFAULT_OBJECTIVE_OPTS = {
    targetSeparability: 2.5,
    magnitudeWeight: 0.35,
    magnitudeRef: 10,
    saturationWeight: 1,
};
export function regularizedObjective(inS, outS, value, opts = DEFAULT_OBJECTIVE_OPTS) {
    const sep = separability(inS, outS);
    const t = opts.targetSeparability > 0 ? sep / opts.targetSeparability : 0;
    const satisfaction = t <= 0 ? 0 : t >= 1 ? 1 + 0.1 * Math.tanh(t - 1) : t;
    const magCost = opts.magnitudeWeight * (value / (opts.magnitudeRef || 1));
    const penalty = opts.saturationWeight * saturationPenalty(inS);
    const score = satisfaction - magCost - penalty;
    return { score, sep, satisfaction, magCost, penalty };
}
function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}
/**
 * Fitness for offline DSP-parameter optimization. Rewards BOTH the ability to
 * separate voice from silence (separability) AND the information content of the
 * in-window (voice) metric distribution — proxied by its standard deviation,
 * normalized to `infoRef` — while penalizing saturation (a pinned, near-1 mean
 * with vanishing spread) and, gently, larger parameter magnitudes.
 */
export function informationObjective(inS, outS, value, opts) {
    const sep = separability(inS, outS);
    const t = opts.targetSeparability > 0 ? sep / opts.targetSeparability : 0;
    const satisfaction = t <= 0 ? 0 : t >= 1 ? 1 + 0.1 * Math.tanh(t - 1) : t;
    const info = clamp01(inS.sigma / (opts.infoRef || 1));
    const penalty = opts.saturationWeight * saturationPenalty(inS);
    const magCost = opts.magnitudeWeight * (value / (opts.magnitudeRef || 1));
    const score = opts.separabilityWeight * satisfaction +
        opts.infoWeight * info -
        penalty -
        magCost;
    return { score, sep, info, satisfaction, penalty, magCost };
}
export class ScalarOptimizer {
    constructor(start, opts) {
        Object.defineProperty(this, "opts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "converged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "best", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "current", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dir", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "step", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "trials", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        this.opts = opts;
        this.current = this.clamp(start);
        this.step = opts.step;
    }
    get candidate() {
        const r = this.opts.round;
        return r ? r(this.current) : this.current;
    }
    get bestValue() {
        const v = this.best ? this.best.value : this.current;
        const r = this.opts.round;
        return r ? r(v) : v;
    }
    get bestResult() {
        return this.best;
    }
    history() {
        return this.trials;
    }
    report(result) {
        this.trials.push(result);
        const first = this.best === null;
        const improved = !first && result.score > this.best.score + 1e-4;
        if (first || improved) {
            this.best = result;
            this.current = this.clamp(this.mul(this.current, this.dir));
        }
        else {
            this.dir = this.dir === 1 ? -1 : 1;
            this.step = Math.max(this.opts.minStep, 1 + (this.step - 1) * 0.6);
            const from = this.best ? this.best.value : this.current;
            this.current = this.clamp(this.mul(from, this.dir));
            if (this.step <= this.opts.minStep + 1e-6) {
                this.converged = true;
                if (this.best)
                    this.current = this.clamp(this.best.value);
            }
        }
        return this.candidate;
    }
    clamp(v) {
        return Math.max(this.opts.min, Math.min(this.opts.max, v));
    }
    mul(v, dir) {
        return dir === 1 ? v * this.step : v / this.step;
    }
}
